import type { Dialog as BaseDialog } from '@base-ui/react';
import type { RefObject } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

import type { ForumAuthor, ForumPost } from '~/api/forum/types';
import type { EditorRefHandle } from '~/components';

import { useCreateForumComment } from '~/api/forum/useCreateForumComment';
import { useForumComments } from '~/api/forum/useForumComments';
import { useForumPostSystemMessages } from '~/api/forum/useForumPostSystemMessages';
import { QueryKeys } from '~/api/keys';
import { useUser } from '~/api/user';
import { Button, Card, ErrorState, Text, Title } from '~/components';
import { toast } from '~/utils/toast';

import type { ForumCommentFieldValues } from '../components/ForumCommentEditorField';

import { CreateForumCommentForm } from '../components/CreateForumCommentForm';
import { ForumComment } from '../components/ForumComment';
import { ForumCommentSkeleton } from '../components/ForumComment.skeleton';
import { ForumPostSystemMessage } from '../components/ForumPostSystemMessage';
import { commentSectionStyle, communityProfileCtaStyle } from './ForumPostDetail.css';

type Props = {
  authorDialogHandle: BaseDialog.Handle<ForumAuthor>;
  commentEditorRef: RefObject<EditorRefHandle | null>;
  forumPost: ForumPost;
  onEnableCommunityProfile: () => void;
};

export const ForumComments = ({ authorDialogHandle, commentEditorRef, forumPost, onEnableCommunityProfile }: Props) => {
  const { data: user } = useUser();
  const { data: comments, status } = useForumComments(forumPost.id);
  const { data: systemMessages } = useForumPostSystemMessages(forumPost.id);
  const createCommentMutation = useCreateForumComment();
  const queryClient = useQueryClient();

  const { t } = useTranslation(['forum']);

  const onSubmit = (data: ForumCommentFieldValues) => {
    createCommentMutation.mutate(
      { content: JSON.stringify(data.content), forumPostId: forumPost.id },
      {
        onSuccess: () => {
          toast.success({ description: t('forum:alerts.createComment.success') });
        },
        onError: () => toast.danger({ description: t('forum:alerts.createComment.error') }),
      },
    );
  };

  const commentsAndMessages = [
    ...(comments ?? []).map((comment) => ({ type: 'comment' as const, data: comment })),
    ...(systemMessages ?? []).map((systemMessage) => ({ type: 'systemMessage' as const, data: systemMessage })),
  ].sort((a, b) => {
    const referenceA = a.type === 'comment' ? a.data.createdAt : a.data.displayedAt;
    const referenceB = b.type === 'comment' ? b.data.createdAt : b.data.displayedAt;

    return dayjs(referenceA).isBefore(referenceB) ? -1 : 1; // Old to new
  });

  return (
    <section className={commentSectionStyle}>
      <Title as="h5">
        {status === 'success' ? t('forum:comments.title', { count: comments.length }) : t('forum:comments.titleUnknown')}
      </Title>

      {status === 'error' && (
        <ErrorState
          onRetry={() => queryClient.resetQueries({ queryKey: QueryKeys.forum.show.comments(forumPost.id) })}
          title={t('forum:comments.error.title')}
        />
      )}

      {status === 'pending' && <ForumCommentSkeleton />}

      {status === 'success' &&
        commentsAndMessages.map(({ data, type }) =>
          type === 'comment' ? (
            <ForumComment authorDialogHandle={authorDialogHandle} comment={data} key={data.id} />
          ) : (
            <ForumPostSystemMessage key={`${data.type} ${data.displayedAt}`} systemMessage={data} />
          ),
        )}

      {!forumPost.locked &&
        (user.communityProfile.enabled ? (
          <CreateForumCommentForm commentEditorRef={commentEditorRef} loading={createCommentMutation.isPending} onSubmit={onSubmit} />
        ) : (
          <Card.Root className={communityProfileCtaStyle} size="small">
            <Text>{t('forum:comments.communityProfileRequired.description')}</Text>

            <Button emphasis="secondary" icon="PersonAdd" onClick={onEnableCommunityProfile}>
              {t('forum:comments.communityProfileRequired.action')}
            </Button>
          </Card.Root>
        ))}
    </section>
  );
};
